/**
 * NotificationList — bildirim kutusu liste gövdesi (N2-02)
 *
 * Salt sunum (presentational): kendi veri çekimi yok — hook'lar bell'de tüketilir,
 * buraya prop olarak geçer (tek sorumluluk + test kolaylığı). Dört durum:
 * yükleniyor (skeleton), hata (mesaj + tekrar-dene), boş (soluk Bell + metin,
 * boş beyaz ekran YASAK — ui-ux-pro-max Empty States), dolu (divide-y liste).
 */
import { Bell } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'
import type { NotificationItem as NotificationItemType } from '@/services/notifications-api'

import { NotificationItem } from './notification-item'

interface NotificationListProps {
  items: NotificationItemType[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  onRead: (id: string) => void
  /** Boş durum metni (N2-03 per-sekme); verilmezse varsayılan "Henüz bildirimin yok". */
  emptyLabel?: string
}

/** Yükleniyor iskeleti — başlık + iki gövde çizgisi, 4 satır. */
function LoadingState() {
  return (
    <div data-testid="notification-loading" className="divide-y divide-border">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="flex flex-col gap-2 px-4 py-3">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  )
}

/** Hata durumu — kısık mesaj + ghost "Tekrar dene" (interface voice, refetch). */
function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      <p className="text-sm text-muted-foreground">
        Bildirimler yüklenemedi, tekrar dene
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md px-3 py-1.5 text-sm font-medium text-brand-purple transition-colors hover:bg-muted"
      >
        Tekrar dene
      </button>
    </div>
  )
}

/** Boş durum — soluk Bell + yardımcı metin (blank ekran değil). */
function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
      <Bell className="size-10 text-muted-foreground/40" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  )
}

export function NotificationList({
  items,
  isLoading,
  isError,
  onRetry,
  onRead,
  emptyLabel,
}: NotificationListProps) {
  if (isLoading) return <LoadingState />
  if (isError) return <ErrorState onRetry={onRetry} />
  if (items.length === 0)
    return <EmptyState label={emptyLabel ?? 'Henüz bildirimin yok'} />

  return (
    <div className="divide-y divide-border">
      {items.map((item) => (
        <NotificationItem key={item.id} notification={item} onRead={onRead} />
      ))}
    </div>
  )
}
